import React, { Component } from 'react';
import '../css/components/bookingListItem.css';
import Image from '../components/skeleton/ImageComponent'
import DOMPurify from 'dompurify';

/**
 * The booking shapes passed in from consuming views (Approvals.jsx,
 * SupplierActiveBookingsLegacy.jsx) are report-specific and include nested
 * account/location objects not present on the shared `Booking` interface.
 * An index signature is required because `extractValue` walks arbitrary
 * dot-separated field paths (e.g. "buyerAccount.companyName") at runtime.
 */
interface BookingListItemBookingData {
    id: string;
    status: string;
    active?: boolean;
    frequency?: string;
    bookingSubscriptionStatus?: string;
    mapsLocationAddress?: string;
    locationAddressLine1?: string;
    locationCity?: string;
    locationState?: string;
    locationInstructions?: string;
    locationHas247Access?: boolean;
    locationGateHours?: string;
    locationAfterHoursAccessProcedure?: string;
    locationListingImageFileName?: string;
    buyerAccount?: { companyName: string };
    supplierAccount?: { companyName: string };
    location?: { locationName: string };
    buyerCompanyName?: string;
    supplierCompanyName?: string;
    locationName?: string;
    [key: string]: unknown;
}

interface BookingListItemLabel {
    label: string | ((booking: BookingListItemBookingData) => string);
    field: string;
    field2?: string;
    valueF?: (booking: BookingListItemBookingData) => string;
    class?: string;
    rows?: number;
}

interface BookingListItemProps {
    account: { type: string };
    labels: BookingListItemLabel[];
    booking: BookingListItemBookingData;
    children?: React.ReactNode;
}

class BookingListItem extends Component<BookingListItemProps> {
    itemImage: string;

    constructor(props: BookingListItemProps) {
        super(props);

        this.itemImage = "https://s3-us-west-1.amazonaws.com/securspace-files/" +
            (props.booking.locationListingImageFileName ? "listing-images/" + encodeURIComponent(props.booking.locationListingImageFileName)
                : "/app-images/not-found-image/place_holder_image.png");
    }

    extractValue = (field: string[]): string => {
        let myValue: unknown = "";
        if (field[0]) {
            myValue = this.props.booking;
            for (let i = 0; i < field.length; i++) {
                myValue = myValue ? (myValue as Record<string, unknown>)[field[i]] : '';
            }
        }
        return (myValue ? myValue : '-') as string;
    };

    getStatusClass(status: string): string {
        if (status === 'Approved' || status === 'Approved-Adjusted' || status === 'Active' || status === 'Active-Adjusted' ||
            status === 'Completed' || status === 'Completed-Adjusted') {
            return 'ss-bookings ss-success-box';
        } else if (status === 'Processing-ACH-Payment' || status === 'Processing-ACH-Payment-Adjusted' ||
            status === 'Processing-ACH-Refund-Adjusted' || status === 'Pending') {
            return 'ss-bookings ss-warning-box';
        } else {
            return 'ss-bookings ss-danger-box';
        }
    }

    extractField = (item: BookingListItemLabel): string => {
        if (typeof item.valueF !== 'undefined') {
            const valueF = item.valueF(this.props.booking);
            return valueF ? valueF : '-';
        } else {
            const field = item.field.split(".");
            const myValue = this.extractValue(field);
            let myValue2 = "";

            if (typeof item.field2 !== 'undefined') {
                const field2 = item.field2.split(".");
                myValue2 = this.extractValue(field2);
                if (myValue2 === '12/31/2200') {
                    myValue2 = '<span style="line-height: .1;position: relative;font-size: 12px">Until cancelled</span>';
                }
            }
            return myValue + (myValue2 ? ' &rarr; ' + myValue2 : '');
        }
    };

    applyLabel = (item: BookingListItemLabel): string => {
        if (typeof item.label === 'string') {
            return item.label;
        } else if (typeof item.label === 'function') {
            return item.label(this.props.booking);
        } else return 'invalid!!!';
    };

    applyClass = (item: BookingListItemLabel): string => {
        const myValue = this.extractField(item);
        const classMethodName = item.class + '';
        const classMethod = (this as unknown as Record<string, (value: string) => string>)[classMethodName];
        return classMethod(myValue);
    };
    createContentStep = (flag?: number): string => {
        let content = "";
        let startColumn = 0;

        for (let i = ((flag === 2) ? 2 : 0); i < this.props.labels.length && (flag === 1 ? (i < 2) : true); i++) {
            const item = this.props.labels[i];
            if (i === 0 || startColumn === 0) {
                content += "<div>";
            }
            const label = this.applyLabel(item);

            if (typeof item.class !== 'undefined') {
                content += label ? ("<fieldset>" +
                    "<label>" + label + ": </label>" +
                    "<div class='" + this.applyClass(item) + "'>" + this.extractField(item) + "</div>" +
                    "</fieldset>") : "";
            } else if (item.label === 'Address') {
                const { booking } = this.props
                if (['Approved', 'Approved-Adjusted', 'Processing-ACH-Payment', 'Manual-Payment'].includes(booking.status) && booking.active) {
                    content += "<fieldset>"
                        + "<label>" + item.label + "</label>"
                        + "<div>"
                        + `<a href=${booking.mapsLocationAddress} target="_blank">${booking.locationAddressLine1} ${booking.locationCity} ${booking.locationState}</a>`
                        + "</div>"
                        + "</fieldset>"
                }
            } else if (item.label === 'Facility Instructions') {
                const { booking } = this.props
                if (['Approved', 'Approved-Adjusted', 'Processing-ACH-Payment', 'Manual-Payment'].includes(booking.status) && booking.active) {
                    content += "<fieldset>"
                        + `<label>${item.label}</label>`
                        + "<div>"
                        + `<div>${booking.locationInstructions ? booking.locationInstructions : ''}</div>`
                        + "</div>"
                        + "</fieldset>"
                    if (booking.locationHas247Access === false && booking.locationGateHours) {
                        content += "<fieldset>"
                            + "<label>Gate Hours</label>"
                            + "<div>"
                            + `<div>${booking.locationGateHours}</div>`
                            + "</div>"
                            + "</fieldset>"
                    }
                    if (booking.locationHas247Access === false && booking.locationAfterHoursAccessProcedure) {
                        content += "<fieldset>"
                            + "<label>After-Hours Access Procedure</label>"
                            + "<div>"
                            + `<div>${booking.locationAfterHoursAccessProcedure}</div>`
                            + "</div>"
                            + "</fieldset>"
                    }
                }
            } else {
                content += label ? ("<fieldset>" +
                    "<label>" + label + ": </label>" +
                    "<div>" + this.extractField(item) + "</div>" +
                    "</fieldset>") : "";
            }

            startColumn++;
            //end column
            if (item.rows === 2 || startColumn === 2) {
                content += "</div>";
                startColumn = 0;
            }

        }
        return DOMPurify.sanitize(content);
    };

    getBuyerCompanyName = (): string | undefined => {
        return this.props.booking.buyerAccount ?
            this.props.booking.buyerAccount.companyName
            :
            this.props.booking.buyerCompanyName;
    };

    getSupplierCompanyName = (): string | undefined => {
        return this.props.booking.supplierAccount ?
            this.props.booking.supplierAccount.companyName
            :
            this.props.booking.supplierCompanyName;
    };

    getLocationName = (): string | undefined => {
        return this.props.booking.location ?
            this.props.booking.location.locationName
            :
            this.props.booking.locationName;
    };

  render() {
    const $ = (window as unknown as { $: (target: Window) => { width: () => number } }).$;
        const width = $(window).width();
        return (
            <div>
                {
                    (width > 999) ?
                        <div className="booking-list-item">
                            <div className="for-img">
                                <Image src={this.itemImage} errorImg='/app-images/not-found-image/place_holder_image.png' placeHolderimg="https://miro.medium.com/v2/resize:fit:512/0*pyRw1qikTI1eqGm9.gif" alt="Invoice Image" className='ss-invoice-img'  />
                            </div>
                            <div className="for-content">
                                <div className="booking-list-title">
                                    <div>
                                        {
                                            this.props.account.type === 'Supplier' ?
                                                <span style={{ width: "200px" }}>{this.getBuyerCompanyName()}</span>
                                                :
                                                <span
                                                    style={{ width: "200px" }}>{this.getSupplierCompanyName()} - {this.getLocationName()}</span>
                                        }
                                        {
                                            this.props.booking.bookingSubscriptionStatus === 'ACTIVE' && this.props.booking.status === 'Pending' ?
                                                <div style={{ color: "#F09019", fontWeight: "bold" }}>Subscription Pending
                                                    Approval</div>
                                                :
                                                this.props.booking.bookingSubscriptionStatus === 'DECLINED' ?
                                                    <div style={{ color: "red", fontWeight: "bold" }}>Subscription
                                                        Declined</div>
                                                    :
                                                    this.props.booking.bookingSubscriptionStatus === 'ACTIVE' ?
                                                        <div style={{ color: "#42C655", fontWeight: "bold" }}>Subscription
                                                            Active</div>
                                                        :
                                                        this.props.booking.bookingSubscriptionStatus === 'CANCELLED' ?
                                                            <div style={{ color: "red", fontWeight: "bold" }}>Subscription
                                                                Cancelled</div>
                                                            :
                                                            ''
                                        }
                                    </div>
                                    <span>{this.props.children}</span>
                                </div>
                                <div>
                                    <div className="flex"
                                        dangerouslySetInnerHTML={{ __html: this.createContentStep() }} />
                                </div>
                            </div>
                        </div>
                        :
                        <div className="booking-list-item">
                            <div className="row1 flex">
                                <div className="for-img">
                                    <div style={{
                                        backgroundImage: "url(" + this.itemImage + ")"
                                    }}>
                                    </div>
                                </div>
                                <div className="booking-list-title">
                                    {
                                        this.props.account.type === 'Supplier' ?
                                            <span>{this.getBuyerCompanyName()}<br />{this.getLocationName()}</span>
                                            :
                                            <span>{this.getSupplierCompanyName()}<br />{this.getLocationName()}</span>
                                    }
                                    <div className="for-content">
                                        <div>
                                            <div className="flex"
                                                dangerouslySetInnerHTML={{ __html: this.createContentStep(1) }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row2 flex" dangerouslySetInnerHTML={{ __html: this.createContentStep(2) }}>

                            </div>
                            {this.props.children}
                        </div>
                }
            </div>
        )

    }
}

export default BookingListItem;
